import { cn } from '@/lib/utils'
import { TrocaInicioPonto } from '@/service/api'
import { PeriodoPontoFactory } from '@acme/common'
import dayjs from 'dayjs'
import { CalendarIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useEditarEmpresaOutletContext } from '../../../modals/Editar'

export default function BlocosFechamento({
    trocasInicioPonto,
}: {
    trocasInicioPonto: TrocaInicioPonto[]
}) {
    const { watch, setValue } = useFormContext()
    const maximoDias = watch('maximoDiasCompetencia')

    const {
        data: { competenciaAtual, diaInicioPonto = 1 },
    } = useEditarEmpresaOutletContext()
    const novoDiaInicioPonto = watch('diaInicioPonto', diaInicioPonto)
    const existeTroca = trocasInicioPonto.some(
        it => it.competencia === competenciaAtual,
    )

    const { competencia, periodoAtual, proximoPeriodo, periodoSeguinte } =
        useMemo(() => {
            const competencia = dayjs.utc(competenciaAtual, 'YYYY-MM')
            const [inicio] = PeriodoPontoFactory.fromCompetencia(
                competencia.format('YYYY-MM'),
                {
                    diaInicioPonto,
                    trocasInicioPonto: trocasInicioPonto.map(it => ({
                        ...it,
                        dataInicial: dayjs.utc(it.dataInicial).toDate(),
                        dataFinal: dayjs.utc(it.dataFinal).toDate(),
                    })),
                },
            ).getPeriodo()

            const fim = inicio.add(maximoDias, 'days').subtract(1, 'day')

            const proximoInicio = fim.add(1, 'day')
            let proximoFim = proximoInicio
                .set('date', Number(novoDiaInicioPonto))
                .set('month', proximoInicio.month() + 1)
                .subtract(1, 'day')
            if (proximoFim.diff(proximoInicio, 'day') + 1 > 45) {
                proximoFim = proximoFim.subtract(
                    proximoFim.diff(proximoInicio, 'day') + 1 - 45,
                    'day',
                )
            }

            const inicioSeguinte = proximoFim.add(1, 'day')
            const fimSeguinte = inicioSeguinte
                .set('date', Number(novoDiaInicioPonto))
                .set('month', inicioSeguinte.month() + 1)
                .subtract(1, 'day')

            return {
                competencia,
                periodoAtual: [inicio, fim],
                proximoPeriodo: [proximoInicio, proximoFim],
                periodoSeguinte: [inicioSeguinte, fimSeguinte],
            }
        }, [competenciaAtual, diaInicioPonto, maximoDias, novoDiaInicioPonto])

    useEffect(() => {
        setValue('dataInicial', periodoAtual[0].toDate())
        setValue('dataFinal', periodoAtual[1].toDate())
        setValue('proximaDataInicial', proximoPeriodo[0].toDate())
        setValue('proximaDataFinal', proximoPeriodo[1].toDate())
    }, [periodoAtual, proximoPeriodo])

    const hidePreview =
        !existeTroca &&
        (!novoDiaInicioPonto || diaInicioPonto === novoDiaInicioPonto)

    return [
        <span
            className={cn(
                'text-sm text-secondary-foreground',
                'opacity-0 max-h-0 transition-[opacity_height] duration-200',
                {
                    'opacity-100 max-h-unset': hidePreview,
                },
            )}
            key="cant-show"
        >
            Altere o campo "Novo dia de início do ponto" para ver uma prévia das
            competências.
        </span>,
        <div
            className={cn(
                'flex flex-row items-center gap-4',
                'opacity-0 transition-opacity duration-200',
                '-mt-4',
                {
                    'opacity-100': !hidePreview,
                },
            )}
            key="atual"
        >
            <CalendarIcon className="size-8" />
            <p className="flex flex-col">
                <span>
                    Competência atual: {competencia.format('MM/YYYY - MMMM')}
                </span>
                <span className="text-secondary-foreground text-sm">
                    De {periodoAtual[0].format('DD/MM/YYYY')} até{' '}
                    {periodoAtual[1].format('DD/MM/YYYY')} (
                    {periodoAtual[1].diff(periodoAtual[0], 'day') + 1} dias){' '}
                </span>
            </p>
        </div>,
        <div
            className={cn(
                'flex flex-row items-center gap-4',
                'opacity-0 transition-opacity duration-200',
                {
                    'opacity-100': !hidePreview,
                },
            )}
            key="proximo"
        >
            <CalendarIcon className="size-8" />
            <p className="flex flex-col">
                <span>
                    Próxima competência:{' '}
                    {competencia.add(1, 'month').format('MM/YYYY - MMMM')}
                </span>
                <span className="text-secondary-foreground text-sm">
                    De {proximoPeriodo[0].format('DD/MM/YYYY')} até{' '}
                    {proximoPeriodo[1].format('DD/MM/YYYY')} (
                    {proximoPeriodo[1].diff(proximoPeriodo[0], 'day') + 1} dias){' '}
                </span>
            </p>
        </div>,
        <div
            className={cn(
                'flex flex-row items-center gap-4',
                'opacity-0 transition-opacity duration-200',
                {
                    'opacity-100': !hidePreview,
                },
            )}
            key="seguinte"
        >
            <CalendarIcon className="size-8" />
            <p className="flex flex-col">
                <span>
                    Competência seguinte:{' '}
                    {competencia.add(2, 'month').format('MM/YYYY - MMMM')}
                </span>
                <span className="text-secondary-foreground text-sm">
                    De {periodoSeguinte[0].format('DD/MM/YYYY')} até{' '}
                    {periodoSeguinte[1].format('DD/MM/YYYY')} (
                    {periodoSeguinte[1].diff(periodoSeguinte[0], 'day') + 1}{' '}
                    dias){' '}
                </span>
            </p>
        </div>,
    ]
}
